import {QueryClient, UseQueryResult, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import IWBrokerClient from '../client/IWBrokerClient';
import {gatewayRealtimeQueryOptions} from './gatewayQueryUtils';

export type Relay = Record<string, any> & {id: string | number};
export type Relays = Record<string, Relay>;

export const relaysQueryKey = ['relay list'] as const;

const installedRelaysListeners = new WeakSet<QueryClient>();

function installRelaysListener(queryClient: QueryClient) {
    if (installedRelaysListeners.has(queryClient)) {
        return;
    }

    IWBrokerClient.socket.on('relay update', (relay: Relay) => {
        console.log('(relaysQuery) relay update', relay);
        queryClient.setQueryData<Relays>(relaysQueryKey, (current: Relays | undefined) => ({
            ...current,
            [relay.id]: relay
        }));
    });

    installedRelaysListeners.add(queryClient);
}

export function useRelaysQuery(): UseQueryResult<Relays, Error> {
    const queryClient = useQueryClient();
    installRelaysListener(queryClient);

    return useQuery<Relays>({
        queryKey: relaysQueryKey,
        queryFn: async () => IWBrokerClient.request('relay list') as Promise<Relays>,
        ...gatewayRealtimeQueryOptions
    });
}

interface SetRelayStateVariables {
    id: string | number;
    state: boolean;
}

export function useSetRelayStateMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['relay state'],
        mutationFn: async ({id, state}: SetRelayStateVariables) => {
            await IWBrokerClient.request('relay state', {id, state});
        },
        onMutate: async ({id, state}) => {
            await queryClient.cancelQueries({queryKey: relaysQueryKey});
            const previousRelays = queryClient.getQueryData<Relays>(relaysQueryKey);

            queryClient.setQueryData<Relays>(relaysQueryKey, (current: Relays | undefined) => {
                if (!current?.[id]) {
                    return current;
                }

                return {
                    ...current,
                    [id]: {
                        ...current[id],
                        state
                    }
                };
            });

            return {previousRelays};
        },
        onError: (_error, _variables, context) => {
            if (context?.previousRelays) {
                queryClient.setQueryData(relaysQueryKey, context.previousRelays);
            }
        }
    });
}
